// src/Components/Chart_Support_Files/chartConfig.js

import CustomDataFeed from '../CustomDataFeed';
import { addCustomButtons } from '../chartButtons';
import { getCustomIndicators } from './chartIndicatorGetters';

const CHART_COLORS = {
  bg: '#05070c',
  pane: '#000000',
  panel: '#0d121c',
  text: '#f4f7fb',
  mutedText: '#a9b3c4',
  grid: 'rgba(255,255,255,0.035)',
  border: 'rgba(255,255,255,0.1)',
  gold: '#d4af37',
  goldBright: '#f6d36b',
  crosshair: 'rgba(234,239,247,0.75)',
};

export function buildWidgetOptions({
  chartContainerRef,
  indexData,
  dynamicLevels,
  topOptions,
  greekExposures,
  gexTrendStrength,
  isMobile,
}) {
  return {
    container: chartContainerRef.current,

    datafeed: new CustomDataFeed({
      indexData,
      dynamicLevels,
      topOptions,
      greekExposures,
    }),

    interval: '1',
    symbol: 'SPX',
    library_path: '/charting_library/',
    autosize: true,

    // Leaving this off gives more control, but requires deeper overrides.
    // theme: 'Dark',

    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    supported_resolutions: ['1', '3', '5', '10'],

    symbols_list: [
      {
        symbol: 'SPX',
        description: 'S&P 500 Index',
      },
      {
        symbol: 'ES',
        description: 'E-mini S&P 500 Futures',
      },
    ],

    overrides: {
      // Main pane background
      'paneProperties.background': CHART_COLORS.pane,
      'paneProperties.backgroundType': 'solid',
      'paneProperties.backgroundGradientStartColor': CHART_COLORS.pane,
      'paneProperties.backgroundGradientEndColor': CHART_COLORS.pane,

      // Remove white internal grid/borders
      'paneProperties.vertGridProperties.color': CHART_COLORS.grid,
      'paneProperties.horzGridProperties.color': CHART_COLORS.grid,
      'paneProperties.crossHairProperties.color': CHART_COLORS.crosshair,

      // Scales / price axis / time axis
      'scalesProperties.textColor': CHART_COLORS.text,
      'scalesProperties.lineColor': CHART_COLORS.border,
      'scalesProperties.backgroundColor': CHART_COLORS.pane,
      'scalesProperties.fontSize': 11,

      // Session / pane separators
      'chartProperties.paneProperties.borderColor': CHART_COLORS.border,

      // Candles
      'mainSeriesProperties.candleStyle.upColor': '#57ff68',
      'mainSeriesProperties.candleStyle.downColor': '#ff604b',
      'mainSeriesProperties.candleStyle.borderUpColor': '#57ff68',
      'mainSeriesProperties.candleStyle.borderDownColor': '#ff604b',
      'mainSeriesProperties.candleStyle.wickUpColor': '#57ff68',
      'mainSeriesProperties.candleStyle.wickDownColor': '#ff604b',

      // No visible volume default
      'volumePaneSize': 'tiny',

    },

    studies_overrides: {
      // Study pane styling
      'paneProperties.background': CHART_COLORS.pane,
      'paneProperties.backgroundType': 'solid',
      'paneProperties.vertGridProperties.color': CHART_COLORS.grid,
      'paneProperties.horzGridProperties.color': CHART_COLORS.grid,

      // Study text / scales
      'scalesProperties.textColor': CHART_COLORS.text,
      'scalesProperties.lineColor': CHART_COLORS.border,

      // Common volume colors if any volume-like study appears
      'volume.volume.color.0': 'rgba(255,96,75,0.35)',
      'volume.volume.color.1': 'rgba(87,255,104,0.35)',
    },

    disabled_features: isMobile
      ? [
          'scroll_past_end',
          'create_volume_indicator_by_default',
          'control_bar',
          'header_widget',
          'left_toolbar',
          'timeframes_toolbar',
          'edit_buttons_in_legend',
          'context_menus',
          'border_around_the_chart',
          'mouse_wheel_scroll',
        ]
      : [
          'scroll_past_end',
          'create_volume_indicator_by_default',
          'border_around_the_chart',
          'mouse_wheel_scroll',
        ],

    enabled_features: [
      'shift_visible_range_on_new_bar',
      'mouse_wheel_scale',
      'pinch_scale',
      'axis_pressed_mouse_move_scale',
      'move_logo_to_main_pane',
      'save_chart_properties_to_local_storage', // persists user drawings + zoom across reload
    ],
    // NOTE: targeted persistence -- drawings/zoom survive reload; study_templates stays OFF
    // so TradingView does NOT restore saved studies (the dashboard auto-loads its own via
    // loadDefaultIndicators each session, avoiding duplicates). The right-edge pin race
    // (saved-view restore is async, fights the right-edge pin) is mitigated by
    // ensureRightEdge() in onChartReady: dataReady callback + setTimeout(1500) re-assert.
    // use_localstorage_for_settings and study_templates remain OFF intentionally.

    custom_indicators_getter: async (PineJS) =>
      getCustomIndicators({
        PineJS,
        dynamicLevels,
        topOptions,
        greekExposures,
        gexTrendStrength,
      }),

    // The session range can contain 390 one-minute bars. TradingView silently
    // shortens setVisibleRange requests when this floor is too large for a
    // narrow pane.
    time_scale: {
      min_bar_spacing: 0.05,
    },

    timeScale: {
      timeVisible: true,
      secondsVisible: false,
      rightOffset: 10,
      barSpacing: 6,
      fixLeftEdge: true,
      fixRightEdge: true,
      lockVisibleTimeRangeOnResize: false,
      rightBarStaysOnScroll: true,
    },

    loading_screen: {
      backgroundColor: CHART_COLORS.bg,
      foregroundColor: CHART_COLORS.goldBright,
    },

  };
}

export function applyChartStyling(widget) {
  widget.applyOverrides({
    // Main chart background
    'paneProperties.background': CHART_COLORS.pane,
    'paneProperties.backgroundType': 'solid',
    'paneProperties.backgroundGradientStartColor': CHART_COLORS.pane,
    'paneProperties.backgroundGradientEndColor': CHART_COLORS.pane,

    // Internal chart lines
    'paneProperties.horzGridProperties.color': CHART_COLORS.grid,
    'paneProperties.vertGridProperties.color': CHART_COLORS.grid,
    'chartProperties.paneProperties.borderColor': CHART_COLORS.border,

    // Axis / labels
    'scalesProperties.textColor': CHART_COLORS.text,
    'scalesProperties.lineColor': CHART_COLORS.border,
    'scalesProperties.backgroundColor': CHART_COLORS.pane,

    // Crosshair
    'crossHairProperties.color': CHART_COLORS.crosshair,
    'paneProperties.crossHairProperties.color': CHART_COLORS.crosshair,
  });

  // TradingView CSS variables.
  // These help restyle the UI shell when theme is not set to Dark.
  widget.setCSSCustomProperty('--tv-color-platform-background', CHART_COLORS.bg);
  widget.setCSSCustomProperty('--tv-color-pane-background', CHART_COLORS.pane);
  widget.setCSSCustomProperty('--tv-color-pane-background-secondary', CHART_COLORS.panel);

  widget.setCSSCustomProperty('--tv-color-toolbar-button-background-hover', 'rgba(212,175,55,0.14)');
  widget.setCSSCustomProperty('--tv-color-toolbar-button-background-expanded', 'rgba(212,175,55,0.1)');
  widget.setCSSCustomProperty('--tv-color-toolbar-button-background-active', 'rgba(212,175,55,0.14)');

  widget.setCSSCustomProperty('--tv-color-toolbar-button-text', CHART_COLORS.text);
  widget.setCSSCustomProperty('--tv-color-toolbar-button-text-hover', CHART_COLORS.goldBright);
  widget.setCSSCustomProperty('--tv-color-toolbar-button-text-active', CHART_COLORS.goldBright);

  widget.setCSSCustomProperty('--tv-color-item-active-text', CHART_COLORS.goldBright);
  widget.setCSSCustomProperty('--tv-color-item-hover', 'rgba(212,175,55,0.14)');
  widget.setCSSCustomProperty('--tv-color-popup-background', '#0d121c');
  widget.setCSSCustomProperty('--tv-color-popup-element-text', CHART_COLORS.text);
  widget.setCSSCustomProperty('--tv-color-popup-element-text-hover', CHART_COLORS.goldBright);
  widget.setCSSCustomProperty('--tv-color-popup-element-background-hover', 'rgba(212,175,55,0.14)');

  widget.setCSSCustomProperty('--tv-color-border', CHART_COLORS.border);
}

export function attachCustomButtons({
  widget,
  memberId,
  tvWidgetRef,
  isMobile,
}) {
  if (isMobile) return;

  const opts = widget._options || {};
  const headerOff =
    opts.hide_top_toolbar ||
    (opts.disabled_features || []).includes('header_widget') ||
    !widget.headerReady;

  if (headerOff) return;

  widget.headerReady().then(() => {
    addCustomButtons(widget, memberId, tvWidgetRef);
  });
}
